import message from "@/utils/globalMessage";
import React, { useEffect, useState } from 'react';
import { Card, List, InputNumber, Button, Typography, Spin, Divider } from "antd";
import { useRequest } from 'ahooks';
import { api } from '@redux/api';

const { Title, Text } = Typography;

const CompanySetting = ({ companyId }) => {
    const [configs, setConfigs] = useState([]);

    // 获取优先级配置
    const { loading, run: fetchConfigs } = useRequest(
        () => api.getCompanyPriorityConfig(companyId),
        {
            manual: true,
            onSuccess: (res) => {
                if (res.status === 200 && res.data?.configs) {
                    // 确保按照 Urgent, Important, Low 的顺序排序
                    const sortedConfigs = sortConfigs(res.data.configs);
                    setConfigs(sortedConfigs);
                }
            },
            onError: (error) => {
                message.error('Failed to load priority configuration.');
                console.error(error);
            }
        }
    );

    useEffect(() => {
        if (companyId) {
            fetchConfigs();
        }
    }, [companyId]);

    // 排序辅助函数
    const sortConfigs = (list) => {
        const order = { 'Urgent': 1, 'Important': 2, 'Low': 3 };
        return list.sort((a, b) => (order[a.priority] || 99) - (order[b.priority] || 99));
    };

    // 处理延迟天数变更
    const handleDelayChange = (id, value) => {
        const newConfigs = configs.map(config => 
            config.id === id ? { ...config, delay_day: value } : config
        );
        setConfigs(newConfigs);
    };

    // 批量保存配置 - 使用 Promise.all 并发发送多个请求
    const handleSaveAll = async () => {
        try {
            // 为每个配置创建一个保存请求
            const savePromises = configs.map(config => {
                const payload = {
                    company_id: companyId,
                    priority: config.priority,
                    delay_day: config.delay_day
                };
                return api.setCompanyPriorityConfig(payload);
            });
            
            // 并发执行所有请求
            const results = await Promise.all(savePromises);
            
            // 检查所有请求是否都成功
            const allSuccess = results.every(res => res.status === 200);
            
            if (allSuccess) {
                message.success('Successfully updated all priority configurations.');
                fetchConfigs(); // 重新加载以确保数据同步
            } else {
                message.warning('Some configurations failed to update.');
                fetchConfigs(); // 仍然重新加载以查看哪些成功了
            }
        } catch (error) {
            message.error('An error occurred while saving.');
            console.error(error);
            fetchConfigs(); // 即使出错也重新加载以查看当前状态
        }
    };

    // 检查是否所有配置都有效（delay_day >= 0）
    const isAllConfigsValid = configs.every(config => config.delay_day >= 0);

    return (
        <div style={{ padding: '20px' }}>
            <Card title="Work Order Priority Settings" bordered={false}>
                <Spin spinning={loading}>
                    <List
                        itemLayout="horizontal"
                        dataSource={configs}
                        renderItem={item => (
                            <List.Item>
                                <List.Item.Meta
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ width: '100px', fontWeight: 'bold' }}>
                                                {item.priority_display}
                                            </span>
                                            {getPriorityTag(item.priority)}
                                        </div>
                                    }
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                                    <Text>Days:</Text>
                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        value={item.delay_day}
                                        onChange={(value) => handleDelayChange(item.id, value)}
                                        style={{ width: '100px' }}
                                    />
                                </div>
                            </List.Item>
                        )}
                    />
                    
                    <Divider />
                    
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        <Button 
                            type="primary" 
                            size="large"
                            onClick={handleSaveAll}
                            disabled={!isAllConfigsValid || configs.length === 0}
                            style={{ padding: '0 20px' }}
                        >
                            Save All Settings
                        </Button>
                    </div>
                </Spin>
            </Card>
        </div>
    );
};

// 优先级标签辅助函数
const getPriorityTag = (priority) => {
    let color = 'blue';
    let label = priority;

    switch (priority) {
        case 'Urgent':
            color = '#f50';
            label = '🔴 Urgent';
            break;
        case 'Important':
            color = '#faad14';
            label = '🟠 Important';
            break;
        case 'Low':
            color = '#87d068';
            label = '🟢 Low';
            break;
        default:
            break;
    }

    return <span style={{ color: color, marginLeft: '10px', fontSize: '12px' }}></span>;
};

export default CompanySetting;

